import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import FormInput from "../Shared/FormInput";
import FormAlert from "../Shared/FormAlert";
import PasswordRequirements from "../Shared/PasswordRequirements";
import { useAuthContext } from "../AuthContext";
import cognitoAuthService from "../../../services/cognitoAuthService";
import i18n from "../../../i18n/i18n";
import { STORAGE_KEY_LANG_MANUAL } from "../../../i18n/i18n";
import "../../../styles/auth.css";

const LoginForm = () => {
  const { t } = useTranslation();
  const { setCurrentView, navigateAfterAuth, externalRedirect, appId } =
    useAuthContext();

  const [loginStep, setLoginStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userPoolConfig, setUserPoolConfig] = useState(null);
  const [challengeData, setChallengeData] = useState(null);
  const [confirmationCode, setConfirmationCode] = useState("");
  const [confirmationComplete, setConfirmationComplete] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordValidation, setPasswordValidation] = useState({
    show: false,
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  const validatePasswordInput = (password) => {
    setPasswordValidation({
      show: password.length > 0,
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
    setNewPassword(password);
  };

  const handleLogin = async () => {
    if (loginStep === 1) {
      if (!email) {
        setError(t('login.pleaseEnterEmail'));
        return;
      }

      try {
        setLoading(true);
        setError("");
        const result = await cognitoAuthService.checkEmail(email, {
          externalRedirect,
          appId,
        });

        // Apply tenant language if user hasn't manually toggled
        const manuallySet = localStorage.getItem(STORAGE_KEY_LANG_MANUAL);
        if (!manuallySet && result.lang) {
          i18n.changeLanguage(result.lang);
        }

        // Unconfirmed user — show confirmation screen
        if (result.userStatus === "UNCONFIRMED") {
          setLoginStep("confirm-account");
          setLoading(false);
          return;
        }

        // External IdP user — redirect immediately
        if (result.isIdpUser) {
          if (result.idpUrl) {
            setUserPoolConfig({
              isIdpUser: true,
              idpName: result.idpName,
              idpUrl: result.idpUrl,
            });
            setLoginStep("redirecting");
            window.location.href = result.idpUrl;
            return;
          }
          // IdP user but no URL configured
          setError(t('login.ssoNotConfigured'));
          setLoading(false);
          return;
        }

        setUserPoolConfig({
          hasIdp: result.hasIdp,
          isIdpUser: result.isIdpUser,
          idpName: result.idpName,
          tenantId: result.tenantId,
          clientId: result.clientId,
          idpUrl: result.idpUrl,
        });
        setLoginStep(2);
      } catch (err) {
        setError(err.message || t('login.failedToCheckEmail'));
      } finally {
        setLoading(false);
      }
    } else if (loginStep === 2) {
      if (!password) {
        setError(t('login.pleaseEnterPassword'));
        return;
      }

      try {
        setLoading(true);
        setError("");
        const result = await cognitoAuthService.signIn(email, password, { appId });

        if (result.requiresChallenge && result.challenge === "NEW_PASSWORD_REQUIRED") {
          setChallengeData({
            session: result.session,
            challengeParameters: result.challengeParameters,
            tenantId: result.tenantId,
          });
          setLoginStep("new-password");
          setLoading(false);
          return;
        }

        if (result.passwordResetRequired) {
          setLoginStep("reset-password");
          setLoading(false);
          return;
        }

        navigateAfterAuth();
      } catch (err) {
        setError(err.message || t('login.signInFailed'));
        setLoading(false);
      }
    }
  };

  const handleGoBack = () => {
    setLoginStep(1);
    setPassword("");
    setError("");
  };

  const handleSetNewPassword = async () => {
    if (!newPassword) {
      setError(t('newPassword.pleaseEnterNewPassword'));
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError(t('newPassword.passwordsDoNotMatch'));
      return;
    }

    try {
      setLoading(true);
      setError("");
      const result = await cognitoAuthService.respondToChallenge({
        challengeName: "NEW_PASSWORD_REQUIRED",
        session: challengeData.session,
        username: email,
        tenantId: challengeData.tenantId,
        newPassword,
        appId,
      });

      if (result.requiresChallenge) {
        setChallengeData({
          session: result.session,
          challengeParameters: result.challengeParameters,
          tenantId: result.tenantId,
        });
        setError(t('newPassword.additionalVerification'));
        setLoading(false);
        return;
      }

      navigateAfterAuth();
    } catch (err) {
      setError(err.message || t('newPassword.failedToSetNewPassword'));
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetCode) {
      setError(t('resetPassword.pleaseEnterResetCode'));
      return;
    }
    if (!newPassword) {
      setError(t('newPassword.pleaseEnterNewPassword'));
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError(t('newPassword.passwordsDoNotMatch'));
      return;
    }

    try {
      setLoading(true);
      setError("");
      await cognitoAuthService.confirmForgotPassword(email, resetCode, newPassword);
      setSuccess(t('resetPassword.passwordResetSuccessSigningIn'));
      // Auto-sign in with the new password
      try {
        const result = await cognitoAuthService.signIn(email, newPassword, { appId });
        if (result.success) {
          navigateAfterAuth();
          return;
        }
      } catch {
        // If auto-sign-in fails, redirect to login
      }
      setSuccess(t('resetPassword.passwordResetSuccessSignIn'));
      setTimeout(() => {
        setLoginStep(1);
        setNewPassword("");
        setConfirmNewPassword("");
        setResetCode("");
        setSuccess("");
      }, 2000);
    } catch (err) {
      setError(err.message || t('resetPassword.passwordResetFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAccount = async () => {
    if (!confirmationCode) {
      setError(t('signup.pleaseEnterConfirmationCode'));
      return;
    }

    try {
      setLoading(true);
      setError("");
      await cognitoAuthService.confirmSignUp(email, confirmationCode);
      setConfirmationComplete(true);
    } catch (err) {
      setError(err.message || t('signup.confirmationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      setError("");
      await cognitoAuthService.resendConfirmationCode(email);
      setSuccess(t('signup.confirmationCodeResent'));
    } catch (err) {
      setError(err.message || t('signup.failedToResendCode'));
    } finally {
      setLoading(false);
    }
  };

  if (confirmationComplete) {
    return (
      <div className="form-container">
        <h3>{t('pendingApproval.title')}</h3>
        <div className="pending-approval-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
        </div>
        <p className="info-text">
          {t('pendingApproval.message')}
        </p>
        <div className="form-footer">
          <button
            type="button"
            onClick={() => {
              setConfirmationComplete(false);
              setConfirmationCode("");
              setLoginStep(1);
            }}
            className="link"
          >
            {t('pendingApproval.backToSignIn')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <h3>{t('login.title')}</h3>
      <FormAlert type="error" message={error} />
      <FormAlert type="success" message={success} />

      {loginStep === 1 && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleLogin();
          }}
        >
          <FormInput
            id="email"
            value={email}
            type="email"
            label={t('login.emailLabel')}
            placeholder={t('login.emailPlaceholder')}
            disabled={loading}
            onChange={setEmail}
          />
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <span className="spinner"></span>
            ) : (
              <span>{t('login.continueButton')}</span>
            )}
          </button>
        </form>
      )}

      {loginStep === "redirecting" && (
        <div className="idp-redirect-message">
          <span className="spinner"></span>
          <p>{t('login.redirectingTo', { idpName: userPoolConfig?.idpName || t('login.redirectingToDefault') })}</p>
        </div>
      )}

      {loginStep === 2 && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleLogin();
          }}
        >
          <div className="login-step2-header">
            <button
              type="button"
              className="btn-back"
              onClick={handleGoBack}
              disabled={loading}
            >
              {t('login.backButton')}
            </button>
            <p className="login-email-display">{email}</p>
          </div>

          <FormInput
            id="password"
            value={password}
            type="password"
            label={t('login.passwordLabel')}
            placeholder={t('login.passwordPlaceholder')}
            disabled={loading}
            onChange={setPassword}
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <span className="spinner"></span>
            ) : (
              <span>{t('login.signInButton')}</span>
            )}
          </button>
        </form>
      )}

      {loginStep === "new-password" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSetNewPassword();
          }}
        >
          <p className="info-text">
            {t('newPassword.tempPasswordInfo')}
          </p>

          <div className="form-group">
            <label htmlFor="new-password">{t('newPassword.newPasswordLabel')}</label>
            <input
              id="new-password"
              value={newPassword}
              type="password"
              className="form-control"
              placeholder={t('newPassword.newPasswordPlaceholder')}
              disabled={loading}
              onChange={(e) => validatePasswordInput(e.target.value)}
            />
            <PasswordRequirements validation={passwordValidation} />
          </div>

          <FormInput
            id="confirm-new-password"
            value={confirmNewPassword}
            type="password"
            label={t('newPassword.confirmNewPasswordLabel')}
            placeholder={t('newPassword.confirmNewPasswordPlaceholder')}
            disabled={loading}
            onChange={setConfirmNewPassword}
          />

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <span className="spinner"></span>
            ) : (
              <span>{t('newPassword.setPasswordButton')}</span>
            )}
          </button>
        </form>
      )}

      {loginStep === "reset-password" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleResetPassword();
          }}
        >
          <p className="info-text">
            {t('resetPassword.adminResetInfo')}
          </p>

          <FormInput
            id="reset-code"
            value={resetCode}
            type="text"
            label={t('resetPassword.verificationCodeLabel')}
            placeholder={t('resetPassword.verificationCodePlaceholder')}
            disabled={loading}
            onChange={setResetCode}
          />

          <div className="form-group">
            <label htmlFor="reset-new-password">{t('newPassword.newPasswordLabel')}</label>
            <input
              id="reset-new-password"
              value={newPassword}
              type="password"
              className="form-control"
              placeholder={t('newPassword.newPasswordPlaceholder')}
              disabled={loading}
              onChange={(e) => validatePasswordInput(e.target.value)}
            />
            <PasswordRequirements validation={passwordValidation} />
          </div>

          <FormInput
            id="reset-confirm-password"
            value={confirmNewPassword}
            type="password"
            label={t('newPassword.confirmNewPasswordLabel')}
            placeholder={t('newPassword.confirmNewPasswordPlaceholder')}
            disabled={loading}
            onChange={setConfirmNewPassword}
          />

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <span className="spinner"></span>
            ) : (
              <span>{t('resetPassword.resetPasswordButton')}</span>
            )}
          </button>
        </form>
      )}

      {loginStep === "confirm-account" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleConfirmAccount();
          }}
        >
          <button
            type="button"
            className="btn-back"
            onClick={handleGoBack}
            disabled={loading}
          >
            {t('login.backButton')}
          </button>

          <p className="info-text">
            {t('login.confirmAccountInfo')}
          </p>

          <FormInput
            id="login-confirm-code"
            value={confirmationCode}
            type="text"
            label={t('signup.confirmationCodeLabel')}
            placeholder={t('signup.confirmationCodePlaceholder')}
            disabled={loading}
            onChange={setConfirmationCode}
          />
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? <span className="spinner"></span> : <span>{t('signup.confirmButton')}</span>}
          </button>
          <div className="form-footer">
            <button
              type="button"
              onClick={handleResendCode}
              className="link"
            >
              {t('signup.resendCode')}
            </button>
          </div>
        </form>
      )}

      <div className="form-footer">
        <button
          type="button"
          onClick={() => setCurrentView("forgot-password")}
          className="link"
        >
          {t('login.forgotPassword')}
        </button>
        <button
          type="button"
          onClick={() => setCurrentView("signup")}
          className="link"
        >
          {t('login.createAccount')}
        </button>
      </div>
    </div>
  );
};

export default LoginForm;
